import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Tabs,
  Tab,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { PhysicianGroupType } from "../../../../services/ConfigurationService/types/physicianGroupType";
import {
  useGetPhysiciansByGroupQuery,
  useGetCentersQuery,
  useUnassignPhysicianFromGroupMutation,
} from "../../../../services/ConfigurationService";
import { useGetMyProfileQuery } from "../../../../services/ProfileService";
import { hasPhysicianGroupAccess } from "../../../profile/utils/profileUtils";
import { AssignPhysicianDialog } from "../dialogs/AssignPhysicianDialog";
import { PhysicianGroupMembersGrid } from "../grids/PhysicianGroupMembersGrid";
import { PortalUsersTab } from "../tabs/PortalUsersTab";
import PageCard from "../../../common/components/styled-ui-elements/PageCard";

interface PhysicianGroupDetailViewProps {
  group: PhysicianGroupType;
  onBack: () => void;
}

function GroupIdentifierChips({ group }: { group: PhysicianGroupType }) {
  return (
    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
      <Chip label={group.id} size="small" variant="outlined" />
      {group.phone && <Chip label={group.phone} size="small" variant="outlined" />}
      {group.city && (
        <Chip
          label={`${group.city}${group.state ? ", " + group.state : ""} ${group.zip || ""}`}
          size="small"
          variant="outlined"
        />
      )}
      {group.contactEmail && <Chip label={group.contactEmail} size="small" variant="outlined" />}
    </Stack>
  );
}

export function PhysicianGroupDetailView({ group, onBack }: PhysicianGroupDetailViewProps) {
  const [assignOpen, setAssignOpen] = useState(false);
  const [confirmUnassign, setConfirmUnassign] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const { data: members = [], isLoading } = useGetPhysiciansByGroupQuery({ groupId: group.id });
  const { data: centers = [] } = useGetCentersQuery({ company: group.company });
  const { data: myProfile } = useGetMyProfileQuery();
  const [unassignPhysician] = useUnassignPhysicianFromGroupMutation();
  const showPortalUsersTab = hasPhysicianGroupAccess(myProfile);

  const centerMap = useMemo(() => {
    const map = new Map<string, string>();
    centers.forEach((c) => map.set(c.id, c.shortName || c.name));
    return map;
  }, [centers]);

  const membersWithCenterName = useMemo(
    () => members.map((m) => ({ ...m, centerName: centerMap.get(m.centerId) || m.centerId })),
    [members, centerMap]
  );

  const handleUnassign = async (physicianId: string) => {
    setConfirmUnassign(physicianId);
  };

  const confirmUnassignAction = async () => {
    if (!confirmUnassign) return;
    try {
      await unassignPhysician({ physicianId: confirmUnassign }).unwrap();
    } catch (err) {
      setErrorMessage("Failed to unassign physician.");
    }
    setConfirmUnassign(null);
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "flex-start", minHeight: "100vh", p: 3 }}>
      <PageCard>
        {/* Header */}
        <Box sx={{ p: 3, borderBottom: "2px solid #3b82f6", backgroundColor: "#ffffff" }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <IconButton onClick={onBack}>
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: "#1e293b" }}>
                {group.name}
              </Typography>
              <GroupIdentifierChips group={group} />
            </Box>
            <Button variant="contained" onClick={() => setAssignOpen(true)}>
              Assign Physician
            </Button>
          </Stack>
        </Box>

        {/* Tabs */}
        {showPortalUsersTab ? (
          <>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
                <Tab label="Members" />
                <Tab label="Portal Users" />
              </Tabs>
            </Box>
            {activeTab === 0 && (
              <Box sx={{ p: 3, backgroundColor: "#ffffff" }}>
                <PhysicianGroupMembersGrid
                  members={membersWithCenterName}
                  isLoading={isLoading}
                  onUnassign={handleUnassign}
                />
              </Box>
            )}
            {activeTab === 1 && (
              <PortalUsersTab practiceId={group.id} practiceName={group.name} />
            )}
          </>
        ) : (
          <Box sx={{ p: 3, backgroundColor: "#ffffff" }}>
            <PhysicianGroupMembersGrid
              members={membersWithCenterName}
              isLoading={isLoading}
              onUnassign={handleUnassign}
            />
          </Box>
        )}

        {/* Assign Dialog */}
        <AssignPhysicianDialog
          open={assignOpen}
          onClose={() => setAssignOpen(false)}
          groupId={group.id}
          company={group.company}
        />
      </PageCard>

      {/* Unassign Confirmation Dialog */}
      <Dialog open={!!confirmUnassign} onClose={() => setConfirmUnassign(null)}>
        <DialogTitle>Remove Physician</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove this physician from the group?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmUnassign(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmUnassignAction}>Remove</Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
